// Ported from dev-scrips-pm-react src/patients/components/chart/problems-chart-tab.tsx (PROD-1119 Patients UI port).
/**
 * ProblemsChartTab — Problems inner tab of the patient Chart shell.
 *
 * Flutter ref: `patient_chart/.../tab_widgets/problems_tab.dart` —
 * partitions by `clinicalStatus` into Active (top section) and Resolved
 * (bottom section). Each section is a separate query against
 * `GET /api/Patients/GetConditionStatusDetails?status={Active|Resolved}`.
 *
 * Slice-1 read view + PROD-827 write surface (Add/Edit/Resolve/Delete):
 * - **Add** — POST `/CreateCondition` from the "+" affordance on the
 *   Active section header (Resolved section omits "+" since direct
 *   adding doesn't make clinical sense — resolution flows through the
 *   Resolve row action on an Active row).
 * - **Edit** — PUT `/condition/{id}` with `isDeleted: false` and the
 *   existing `clinicalStatus` preserved.
 * - **Resolve** — PUT with `clinicalStatus: "Resolved"` +
 *   `endDate`/`endDisplayDate` populated. Active-row only.
 * - **Delete** — PUT with `isDeleted: true`. Both sections.
 *
 * `code.code === 'FreeText'` records show `code.display` (the typed
 * string) instead of a coded SNOMED name — Flutter parity.
 *
 * FHIR: `Condition R4`. One row per resource.
 */

// A reported date shows as entered ("about 2023", "Unknown", "2019"), never as its placeholder day.
import { reportedDateLabel } from '@/encounter/lib/reported-date';
import { ChartEmpty } from './chart-empty';
import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import { useState } from 'react';
import { toast } from 'sonner';
import { formatChartDate } from '@/shared/lib/date-format';
import {
  Button,
  FieldLabel,
  Modal,
  ModalYesNo,
  StatusChip,
  TextField,
  Skeleton,
} from '@scripsteam/scrips-signal-ds';
import { todayLocalYmd } from '@/shared/lib/ymd-local';
import { usePatientConditions } from '@/patients/services/use-patient-conditions';
import { useUpdateCondition } from '@/patients/services/use-update-condition';
import { ChartSectionHeader } from './chart-section-header';
import { ChartRowIconActions } from '@/shared/ui/components/chart-row-actions';
import { AddProblemEditor } from './add-problem-editor';
import {
  buildResolveDateFields,
  emptyBodySite,
  isOnsetAfterEnd,
  localNowIso,
  normalizeConditionCode,
} from '@/patients/lib/conditions';
import {
  normalizeCoding,
  normalizePerformerDetails,
} from './add-allergy-dialog';
import {
  CONDITION_STATUS,
  type PatientCondition,
  type UpdateConditionBody,
} from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';
import { CHART_TD_V2, CHART_TH_V2, chartCell, chartTableCard, type ChartCell } from './chart-table-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

export interface ProblemsChartTabProps {
  patientId: string | null | undefined;
}

export function ProblemsChartTab({ patientId }: ProblemsChartTabProps) {
  const activeQ = usePatientConditions(patientId, CONDITION_STATUS.active);
  const resolvedQ = usePatientConditions(patientId, CONDITION_STATUS.resolved);

  const active = (activeQ.data ?? []).filter((c) => c.isDeleted !== true);
  const resolved = (resolvedQ.data ?? []).filter((c) => c.isDeleted !== true);
  // The index's count: every problem on file, active and resolved (nothing while either list loads).
  useReportChartCount(activeQ.isLoading || resolvedQ.isLoading ? null : active.length + resolved.length);

  // Edit / Add / Delete / Resolve state — same "only one surface at a
  // time" rule as Allergies (Flutter parity).
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<PatientCondition | null>(null);
  const [deleting, setDeleting] = useState<PatientCondition | null>(null);
  const [resolving, setResolving] = useState<PatientCondition | null>(null);
  const [resolveDate, setResolveDate] = useState<string>(todayLocalYmd());
  const updateMut = useUpdateCondition();

  const editorOpen = addOpen || editing !== null;

  const closeEditor = () => {
    setAddOpen(false);
    setEditing(null);
  };

  const startEdit = (c: PatientCondition) => {
    setAddOpen(false);
    setEditing(c);
  };

  // Soft-delete + Resolve both use the same PUT — only the body
  // delta differs. Helper builds the full UpdateConditionBody.
  const buildPutBody = (
    c: PatientCondition,
    overrides: Partial<
      Pick<
        UpdateConditionBody,
        | 'isDeleted'
        | 'clinicalStatus'
        | 'endDate'
        | 'endDisplayDate'
        | 'onsetDateTime'
        | 'onsetDisplayDateTime'
      >
    >,
  ): UpdateConditionBody | null => {
    if (!c.identifier) return null;
    return {
      identifier: c.identifier,
      clinicalStatus:
        overrides.clinicalStatus ??
        (c.clinicalStatus === 'Resolved' ? 'Resolved' : 'Active'),
      code: normalizeConditionCode(c.code),
      bodySite: emptyBodySite(c.bodySite),
      subject: c.subject ?? patientId ?? '',
      dataAbsentReason: 'n/a',
      encounter: c.encounter ?? null,
      asserter: c.asserter ?? null,
      endDisplayDate: overrides.endDisplayDate ?? c.endDisplayDate ?? null,
      onsetDisplayDateTime:
        overrides.onsetDisplayDateTime ?? c.onsetDisplayDateTime ?? null,
      onsetDateTime: overrides.onsetDateTime ?? c.onsetDateTime ?? null,
      endDate: overrides.endDate ?? c.endDate ?? null,
      note: c.note ?? '',
      severity: c.severity ?? 'n/a',
      isDeleted: overrides.isDeleted ?? false,
      performerDetails: normalizePerformerDetails(c.performerDetails),
      createdDate: c.createdDate ?? null,
      speciality: normalizeCoding(c.speciality),
    };
  };

  const confirmDelete = async () => {
    if (!deleting?.identifier || !patientId) return;
    // Strict asserter guard (Samer PR #225 review §2) — refuse to
    // soft-delete a record that's missing its original asserter rather
    // than ship null and corrupt the audit trail.
    if (!deleting.asserter) {
      toast.error(
        "Cannot delete — this record is missing its original asserter. Please contact support.",
      );
      return;
    }
    const body = buildPutBody(deleting, { isDeleted: true });
    if (!body) return;
    try {
      await updateMut.mutateAsync({
        patientId,
        conditionId: deleting.identifier,
        body,
      });
      toast.success(
        `Removed problem: ${deleting.code?.display ?? 'Unknown'}`,
      );
      setDeleting(null);
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  const confirmResolve = async () => {
    if (!resolving?.identifier || !patientId) return;
    // Defense-in-depth: refuse to Resolve a row that's already
    // Resolved. UI hides the Resolve button on Resolved-section rows,
    // but if a future refactor accidentally exposes it (or a parallel
    // browser tab fires twice), this guard blocks the double-resolve
    // PUT at the handler level. Samer PR #225 review §4.
    if (resolving.clinicalStatus === 'Resolved') {
      toast.error('This problem is already resolved.');
      setResolving(null);
      return;
    }
    if (!resolving.asserter) {
      toast.error(
        "Cannot resolve — this record is missing its original asserter. Please contact support.",
      );
      return;
    }
    const endPair = buildResolveDateFields(resolveDate);
    if (!endPair) {
      toast.error('Pick a valid end date to resolve this problem.');
      return;
    }
    // F-PAT-272: onset must not be after the resolution (end) date.
    // Flutter `problems_tab.dart:1064-1080` blocks the Done action with this
    // exact toast when `onsetDateTime.isAfter(endDate)`.
    if (isOnsetAfterEnd(resolving.onsetDateTime, resolveDate)) {
      toast.error(
        'Select start date again, as selected end date is prior to start date.',
      );
      return;
    }
    const body = buildPutBody(resolving, {
      clinicalStatus: 'Resolved',
      ...endPair,
    });
    if (!body) return;
    try {
      await updateMut.mutateAsync({
        patientId,
        conditionId: resolving.identifier,
        body,
      });
      toast.success(
        `Resolved: ${resolving.code?.display ?? 'Unknown'}`,
      );
      setResolving(null);
      setResolveDate(todayLocalYmd());
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  // F-PAT-273: move a Resolved problem back to Active. Mirrors Flutter
  // `problems_tab.dart:324-335` — sets clinicalStatus = "Active" and resets
  // onsetDateTime to now(), then PUTs.
  //
  // Samer review #491:
  //  §1 (clock parity): onset uses `localNowIso()` — local wall-clock, the
  //     same basis as Flutter's `DateTime.now()`. Avoids a UTC-vs-local 4h
  //     onset divergence in UAE (UTC+4) on this clinical field.
  //  §2 (full PUT): the body is built by the SAME `buildPutBody` used by the
  //     already-shipped Resolve + Delete — it carries the complete
  //     UpdateConditionBody (asserter, performerDetails/recorder, code,
  //     bodySite, severity, createdDate, speciality, …), so nothing is
  //     silently dropped from the Condition resource.
  //  §3 (no dialog / downstream refs): direct action — it needs no user
  //     input and is reversible, unlike Resolve (needs an end date) and
  //     Delete (destructive). It flips clinicalStatus + onset on THIS
  //     Condition only; it does not cascade to any linked Encounter/Claim
  //     from the client — referential integrity for downstream references is
  //     owned by the backend/FHIR layer. Matches Flutter's dialog-less flow.
  //     If a future NABIDH coupling needs a pre-unresolve guard, that's a
  //     backend/product gate (flagged, not enforced client-side).
  //  `endDate` is intentionally preserved (Flutter doesn't clear it on
  //  unresolve); onsetDisplayDateTime is set to match the new onset.
  const confirmUnresolve = async (c: PatientCondition) => {
    if (!c.identifier || !patientId || updateMut.isPending) return;
    // Defense-in-depth: only Resolved rows can be unresolved.
    if (c.clinicalStatus !== 'Resolved') return;
    if (!c.asserter) {
      toast.error(
        "Cannot unresolve — this record is missing its original asserter. Please contact support.",
      );
      return;
    }
    const now = new Date();
    const onsetDisplayDateTime = `${String(now.getDate()).padStart(2, '0')}/${String(
      now.getMonth() + 1,
    ).padStart(2, '0')}/${now.getFullYear()}`;
    const body = buildPutBody(c, {
      clinicalStatus: 'Active',
      onsetDateTime: localNowIso(),
      onsetDisplayDateTime,
    });
    if (!body) return;
    try {
      await updateMut.mutateAsync({
        patientId,
        conditionId: c.identifier,
        body,
      });
      toast.success(`Moved back to active: ${c.code?.display ?? 'Unknown'}`);
    } catch {
      /* Global toast surfaces failure. */
    }
  };

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to record problems."
          />
      </div>
    );
  }

  const isLoading = activeQ.isLoading || resolvedQ.isLoading;
  const isError = activeQ.isError || resolvedQ.isError;

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load problems."
            ctaLabel="Retry"
            onCtaClick={() => { void activeQ.refetch(); void resolvedQ.refetch(); }}
          />
      </div>
    );
  }

  if (active.length === 0 && resolved.length === 0) {
    return (
      <div className="p-6 space-y-4">
        {addOpen ? (
          <AddProblemEditor patientId={patientId} onClose={closeEditor} />
        ) : (
          <EmptyState
              icon="clipboard"
              title="No problems recorded for this patient."
              ctaLabel="Add problem"
              onCtaClick={() => setAddOpen(true)}
            />
        )}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Inline editor renders ABOVE the Active section — Flutter
       *  parity. Only one editor mounts at a time. */}
      {editorOpen ? (
        <AddProblemEditor
          key={editing?.identifier ?? 'add'}
          patientId={patientId}
          editing={editing}
          onClose={closeEditor}
        />
      ) : null}

      <Section
        title="Active problems"
        rows={active}
        emptyMessage="No active problems."
        isResolvedSection={false}
        editorOpen={editorOpen}
        onAdd={() => setAddOpen(true)}
        onEdit={startEdit}
        onDelete={(c) => setDeleting(c)}
        onResolve={(c) => {
          setResolveDate(todayLocalYmd());
          setResolving(c);
        }}
      />
      <Section
        title="Resolved problems"
        rows={resolved}
        emptyMessage="No resolved problems on file."
        isResolvedSection={true}
        editorOpen={editorOpen}
        onEdit={startEdit}
        onDelete={(c) => setDeleting(c)}
        onUnresolve={(c) => confirmUnresolve(c)}
      />

      {/* Delete confirm — Flutter copy parity. */}
      <ModalYesNo
        open={deleting !== null}
        onCancel={() => setDeleting(null)}
        title="Are you sure?"
        message={
          <>
            Do you want to proceed on deleting{' '}
            <span className={v2Look('font-semibold text-brand-depth', V2_BODY_EMPHASIS)}>
              {deleting?.code?.display ?? 'this problem'}
            </span>
            ?
          </>
        }
        confirmLabel={updateMut.isPending ? 'Deleting…' : 'Delete'}
        destructive
        loading={updateMut.isPending}
        onConfirm={() => {
          // No auto-close (see ModalYesNo behaviour note above) — `confirmDelete`
          // already calls `setDeleting(null)` on success.
          void confirmDelete();
        }}
      />

      {/* Resolve confirm — picks End Date and flips clinicalStatus.
       *  Flutter wires this as a separate inline form in the Resolved
       *  section; we use a confirm dialog for the React UX.
       *
       *  Plain `Modal`, not `ModalYesNo`: confirm is gated on `!resolveDate`
       *  (form validity), independent of `updateMut.isPending`. `ModalYesNo`
       *  only exposes `loading`, which would force a spinner + disable Cancel
       *  even when nothing is loading — just no end date picked yet. Flagged
       *  as a DS gap in the handback (same gap as the AlertDialog→ModalYesNo
       *  cancel-appointment conversion). */}
      <Modal
        open={resolving !== null}
        onClose={() => {
          setResolving(null);
          setResolveDate(todayLocalYmd());
        }}
        title="Resolve problem"
        size="sm"
        footer={
          <>
            <Button
              type="button"
              onClick={() => void confirmResolve()}
              disabled={updateMut.isPending || !resolveDate}
            >
              {updateMut.isPending ? 'Resolving…' : 'Resolve'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={updateMut.isPending}
              onClick={() => {
                setResolving(null);
                setResolveDate(todayLocalYmd());
              }}
            >
              Cancel
            </Button>
          </>
        }
      >
          <p className="text-sm text-text-primary">
            Mark{' '}
            <span className={v2Look('font-semibold text-brand-depth', V2_BODY_EMPHASIS)}>
              {resolving?.code?.display ?? 'this problem'}
            </span>{' '}
            as resolved. Pick the end date below.
          </p>
          <div className="px-2 pb-2">
            <FieldLabel
              htmlFor="resolve-date"

            >
              End date <span className="text-red-700">*</span>
            </FieldLabel>
            <TextField
              id="resolve-date"
              type="date"
              value={resolveDate}
              max={todayLocalYmd()}
              onChange={(e) => setResolveDate(e.target.value)}
              className="mt-1"
            />
          </div>
      </Modal>
    </div>
  );
}

function Section({
  title,
  rows,
  emptyMessage,
  isResolvedSection,
  editorOpen,
  onAdd,
  onEdit,
  onDelete,
  onResolve,
  onUnresolve,
}: {
  title: string;
  rows: PatientCondition[];
  emptyMessage: string;
  isResolvedSection: boolean;
  editorOpen: boolean;
  onAdd?: () => void;
  onEdit: (c: PatientCondition) => void;
  onDelete: (c: PatientCondition) => void;
  onResolve?: (c: PatientCondition) => void;
  onUnresolve?: (c: PatientCondition) => void;
}) {
  const cap = useRowCap(rows);
  return (
    <div>
      <ChartSectionHeader
        title={title}
        count={rows.length}
        showAdd={!isResolvedSection}
        onAdd={editorOpen || !onAdd ? undefined : onAdd}
      />
      {rows.length === 0 ? (
        <ChartEmpty title={emptyMessage} />
      ) : (
        <div className={chartTableCard('rounded-lg border border-border-default overflow-hidden bg-surface-card')}>
          <table className="w-full text-sm">
            <thead className="bg-grey-100">
              <tr className="text-left text-text-muted">
                <Th>#</Th>
                <Th>Problem</Th>
                <Th>Body site</Th>
                <Th>Severity</Th>
                <Th>Onset</Th>
                {isResolvedSection ? <Th>Resolved</Th> : null}
                <Th>Recorder</Th>
                <Th>Note</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {cap.visible.map((c, i) => (
                <tr
                  key={c.identifier ?? `${i}-${c.code?.code ?? 'unknown'}`}
                  className="border-t border-border-default hover:bg-grey-100/50"
                >
                  <Td cell="index">{i + 1}</Td>
                  <Td cell="name">
                    {/* The name in body type at 400 with its code quiet under it (design ChartRecordTable `name`:
                     *  `.primary` + `.code`, "E11.9"), on both paths of the design switch. */}
                    <span>{c.code?.display ?? '—'}</span>
                    {conditionCodeLine(c) ? (
                      <span data-slot="chart-cell-code">{conditionCodeLine(c)}</span>
                    ) : null}
                  </Td>
                  <Td cell="text">{c.bodySite?.display ?? '—'}</Td>
                  <Td cell="short">{severityBadge(c.severity)}</Td>
                  <Td date>{reportedDateLabel(c.onsetDateTime, c.onsetDisplayDateTime, formatDate)}</Td>
                  {isResolvedSection ? (
                    <Td date>{formatDate(c.endDate)}</Td>
                  ) : null}
                  <Td cell="text">{c.performerDetails?.name ?? '—'}</Td>
                  <Td cell="note" tip={c.note?.trim() || '—'}>{c.note?.trim() || '—'}</Td>
                  <Td align="right" cell="actions">
                    <ChartRowIconActions
                      editLabel={`Edit problem: ${c.code?.display ?? 'Unknown'}`}
                      deleteLabel={`Delete problem: ${c.code?.display ?? 'Unknown'}`}
                      resolveLabel={`Resolve problem: ${c.code?.display ?? 'Unknown'}`}
                      onEdit={editorOpen ? undefined : () => onEdit(c)}
                      onDelete={editorOpen ? undefined : () => onDelete(c)}
                      onResolve={
                        // Resolve action visible ONLY when the section
                        // is Active AND the row's clinicalStatus is
                        // still "Active" (data-level guard — survives
                        // future refactors that might restructure the
                        // section split). Plus the editor-open mutex.
                        // Samer PR #225 review §4.
                        !isResolvedSection &&
                        c.clinicalStatus !== 'Resolved' &&
                        !editorOpen &&
                        onResolve
                          ? () => onResolve(c)
                          : undefined
                      }
                      unresolveLabel={`Unresolve problem: ${c.code?.display ?? 'Unknown'}`}
                      onUnresolve={
                        // Unresolve visible ONLY on Resolved-section rows
                        // whose status is actually Resolved, with the same
                        // editor-open mutex as the other row actions.
                        isResolvedSection &&
                        c.clinicalStatus === 'Resolved' &&
                        !editorOpen &&
                        onUnresolve
                          ? () => onUnresolve(c)
                          : undefined
                      }
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChartShowAll cap={cap} label={title.toLowerCase()} />
    </div>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      className={chartCell(
        `px-3 py-2 text-xs font-bold uppercase tracking-wide ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TH_V2,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = 'left',
  date = false,
  cell,
  tip,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  /** What the column holds (the bridge sizes it: index, name, text, short, date, note, actions). */
  cell?: ChartCell;
  /** A note's full text for the app's instant tooltip, shown when the one-line cell is cut off. */
  tip?: string;
  /** A date: kept on one line (the bridge's "patient chart" block, `data-chart-cell="date"`). */
  date?: boolean;
}) {
  return (
    <td
      data-chart-cell={date ? 'date' : cell}
      data-tip={tip}
      className={chartCell(
        `px-3 py-2 align-top ${
          align === 'right' ? 'text-right' : 'text-left'
        }`,
        CHART_TD_V2,
      )}
    >
      {children}
    </td>
  );
}

/** The record's code for the line under its name (ICD-10 / SNOMED), or nothing: a typed free-text problem carries
 *  `code.code === 'FreeText'` (its words are the name), which is no code to show. */
function conditionCodeLine(c: Pick<PatientCondition, 'code'>): string | null {
  const raw = c.code?.code?.trim();
  if (!raw || raw.toLowerCase() === 'freetext' || raw === 'n/a') return null;
  return raw;
}

function severityBadge(raw: string | null | undefined) {
  if (!raw) return <span className="text-text-hint">—</span>;
  const lower = raw.toLowerCase();
  if (lower === 'n/a') return <span className="text-text-hint">—</span>;
  // Severity → StatusChip semantic. Preserves the prior three-shade palette
  // meanings exactly: severe = red (error), moderate = yellow (warning),
  // mild = blue (info — the `info` soft tone is the blue ramp, matching the
  // previous bg-blue-100/text-blue-700), unknown = grey (neutral).
  const semantic = lower.includes('severe')
    ? 'error'
    : lower.includes('moderate')
      ? 'warning'
      : lower.includes('mild')
        ? 'info'
        : 'neutral';
  return (
    <StatusChip tone="soft" semantic={semantic}>
      {raw}
    </StatusChip>
  );
}

/** "03 Sep 2025", the design's chart-table date (ChartRecordTable `date`), whatever the browser's locale. */
function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === 'n/a') return '—';
  return formatChartDate(iso) || '—';
}

// The module-private `todayIso()` helper that used to live here is gone (PROD-1750). Being
// private is exactly why the PROD-1749 fix shipped with no regression guard: covering it in
// place would have meant mounting this whole component. The resolve-date default and the
// `max=` bound now call todayLocalYmd() from @/shared/lib/ymd-local, which is tested directly
// against the 00:00-04:00 Asia/Dubai window that broke it.
